import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Button from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';
import GetAppIcon from '@material-ui/icons/GetApp';
import {
  DetailContext,
  DictionaryObject,
  FilesContext,
  TableFieldCellProps,
  TableFieldCells,
  TableFieldColumn,
  Tooltip,
  useStaticListSource,
} from '@eas/common-web';
import { DeliveredDocument, Document, DocumentAttachment } from '@models';
import { Agenda, DocumentType, EsFileState, EvidenceBrowserUrl } from '@enums';
import { useStaticAttachmentTypes } from './../attachments/attachment-api';
import { Redirect } from './../redirect/redirect';
import { Remove } from './../remove/remove';
import { useUnassignDocumentDialog } from './dialog-actions/unassing-document-hook';

const useStyles = makeStyles(() => ({
  downloadButton: {
    minWidth: '0',
    boxShadow: '0 0 0',
    padding: 0,
    height: 20,
    '& svg': {
      width: 18,
      height: 18,
    },
    '& p': {
      display: 'block',
    },
  },
  downloadHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    height: '100%',
  },
}));

export function getDocumentUrl(type: DocumentType) {
  switch (type) {
    case DocumentType.DELIVERED:
      return EvidenceBrowserUrl.DOCUMENTS_DELIVERED_PROCESSED;
    case DocumentType.INTERNAL:
      return EvidenceBrowserUrl.DOCUMENTS_INTERNAL;
    case DocumentType.OUTGOING:
      return EvidenceBrowserUrl.DOCUMENTS_OUTGOING;

    default:
      return EvidenceBrowserUrl.DOCUMENTS_DELIVERED_PROCESSED;
  }
}

export function useColumns(props: {
  documentTypes: DictionaryObject[];
  documentDeliveryMethods: DictionaryObject[];
  documentDispatchStates: DictionaryObject[];
  onDownloadAttachments?: () => void;
  downloadAttachmentsDisabled?: boolean;
}): TableFieldColumn<Document>[] {
  const intl = useIntl();
  const classes = useStyles();
  const { documentTypes, documentDispatchStates, documentDeliveryMethods } =
    props;
  const { source } = useContext(DetailContext);

  const { callApi: unassignDocument } = useUnassignDocumentDialog();

  const useDocumentTypes = () => useStaticListSource(documentTypes);
  const useDocumentDeliveryMethods = () =>
    useStaticListSource(documentDeliveryMethods);
  const useDocumentDispatchStates = () =>
    useStaticListSource(documentDispatchStates);

  const columns: TableFieldColumn<Document>[] = [
    {
      datakey: 'id',
      name: props.onDownloadAttachments ? (
        <div className={classes.downloadHeader}>
          <Tooltip
            placement="top"
            type="HOVER"
            title={intl.formatMessage({
              id: 'ES__DOCUMENTS__DOWNLOAD_ATTACHMENTS_ZIP',
              defaultMessage: 'Stáhnout přílohy jako ZIP',
            })}
          >
            <span style={{ display: 'inline-flex' }}>
              <Button
                size="small"
                classes={{ root: classes.downloadButton }}
                variant="text"
                disabled={props.downloadAttachmentsDisabled}
                onClick={(event) => {
                  event.stopPropagation();
                  props.onDownloadAttachments?.();
                }}
              >
                <GetAppIcon />
              </Button>
            </span>
          </Tooltip>
        </div>
      ) : (
        ''
      ),
      width: 140,
      CellComponent: function Cell(props: TableFieldCellProps<Document>) {
        const url = getDocumentUrl(props.rowValue.type!);

        const removedFromEsFile = (props.rowValue as DeliveredDocument)
          .removedFromEsFile;

        const isEsFileClosed =
          props.rowValue?.esFile?.state === EsFileState.CLOSED;

        const showRemoveButton =
          props.rowValue.type === DocumentType.DELIVERED &&
          !removedFromEsFile &&
          !isEsFileClosed;

        return (
          <>
            {(props.rowValue.type !== DocumentType.DELIVERED ||
              (props.rowValue.type === DocumentType.DELIVERED &&
                !(props.rowValue as DeliveredDocument).removedFromEsFile)) && (
              <Redirect
                url={url}
                id={props.value}
                showRedirectInCurrent={!props.rowValue.canceledDate}
                showRedirectInNew={!props.rowValue.canceledDate}
              />
            )}
            {showRemoveButton && (
              <Remove
                id={props.value}
                removeDialogTitle={intl.formatMessage({
                  id: 'ES__DOCUMENTS__UNASSIGN__TITLE',
                  defaultMessage: 'Varování',
                })}
                removeDialogText={intl.formatMessage({
                  id: 'ES__DOCUMENTS__UNASSIGN__TEXT',
                  defaultMessage: 'Skutečně chcete odebrat dokument?',
                })}
                removeOnSuccess={source.refresh}
                removeApiCall={unassignDocument}
              />
            )}
          </>
        );
      },
    },
    {
      name: intl.formatMessage({
        id: 'ES__DOCUMENTS__COLUMN__REFERENCE_NUMBER',
        defaultMessage: 'Číslo jednací',
      }),
      datakey: 'referenceNumber',
      sortkey: 'order',
      width: 200,
    },
    {
      name: intl.formatMessage({
        id: 'ES__DOCUMENTS__COLUMN__SUMMARY',
        defaultMessage: 'Stručný obsah',
      }),
      datakey: 'summary',
      sortkey: 'summary',
      width: 200,
    },

    ...(source?.data?.agenda === Agenda.COURT
      ? [
          {
            name: intl.formatMessage({
              id: 'ES__DOCUMENTS__COLUMN__ATTACHMENTS',
              defaultMessage: 'Přílohy',
            }),
            datakey: 'attachmentsLabelRange',
            width: 100,
          },
        ]
      : []),

    {
      name: intl.formatMessage({
        id: 'ES__DOCUMENTS__COLUMN__TYPE',
        defaultMessage: 'Typ',
      }),
      datakey: 'type',
      sortkey: 'type.name',
      width: 200,
      CellComponent: TableFieldCells.useSelectCellFactory(useDocumentTypes),
    },
    {
      name: intl.formatMessage({
        id: 'ES__DOCUMENTS__COLUMN__DELIVERY_METHOD',
        defaultMessage: 'Způsob doručení',
      }),
      datakey: 'deliveryMethod',
      sortkey: 'deliveryMethod.name',
      width: 200,
      CellComponent: TableFieldCells.useSelectCellFactory(
        useDocumentDeliveryMethods
      ),
    },
    {
      name: intl.formatMessage({
        id: 'ES__DOCUMENTS__COLUMN__DISPATCH_STATE',
        defaultMessage: 'Stav vypravení',
      }),
      datakey: 'dispatchState',
      sortkey: 'dispatchState.name',
      width: 200,
      CellComponent: TableFieldCells.useSelectCellFactory(
        useDocumentDispatchStates
      ),
    },
    {
      name: intl.formatMessage({
        id: 'ES__DOCUMENTS__COLUMN__DELIVERY_DATE',
        defaultMessage: 'Datum doručení',
      }),
      datakey: 'deliveryDate',
      sortkey: 'deliveryDate',
      width: 200,
      CellComponent: TableFieldCells.DateCell,
    },
    {
      name: intl.formatMessage({
        id: 'ES__DOCUMENTS__COLUMN__CREATED_DATE',
        defaultMessage: 'Datum vytvoření',
      }),
      datakey: 'createdDate',
      sortkey: 'createdDate',
      width: 200,
      CellComponent: TableFieldCells.DateTimeCell,
    },
    {
      name: intl.formatMessage({
        id: 'ES__DOCUMENTS__COLUMN__INCOMING_NUMBER',
        defaultMessage: 'Došlé číslo',
      }),
      datakey: 'incomingNumber',
      sortkey: 'incomingNumber',
      width: 200,
    },
    {
      name: intl.formatMessage({
        id: 'ES__DOCUMENTS__COLUMN__NOTE',
        defaultMessage: 'Poznámka',
      }),
      datakey: 'note',
      width: 250,
      CellComponent: TableFieldCells.TextCell,
    },
    ...(source?.data?.agenda === Agenda.INF
      ? [
          {
            name: intl.formatMessage({
              id: 'ES__DOCUMENTS__COLUMN__DECISION',
              defaultMessage: 'Správní rozhodnutí',
            }),
            datakey: 'decision',
            width: 100,
            CellComponent: TableFieldCells.BooleanCell,
          },
          {
            name: intl.formatMessage({
              id: 'ES__DOCUMENTS__COLUMN__COMPLIANT',
              defaultMessage: 'Stížnost',
            }),
            datakey: 'complaint',
            width: 100,
            CellComponent: TableFieldCells.BooleanCell,
          },
        ]
      : []),
  ];

  return columns;
}

export function useAttachmentColumns(
  agenda?: Agenda
): TableFieldColumn<DocumentAttachment>[] {
  const intl = useIntl();
  const classes = useStyles();

  const commonColumns: TableFieldColumn<DocumentAttachment>[] = [
    {
      datakey: 'name',
      name: intl.formatMessage({
        id: 'ES__ATTACHMENTS__COLUMN__NAME',
        defaultMessage: 'Název přílohy',
      }),
      width: 200,
      CellComponent: TableFieldCells.TextCell,
    },
    {
      datakey: 'type',
      name: intl.formatMessage({
        id: 'ES__ATTACHMENTS__COLUMN__TYPE',
        defaultMessage: 'Typ',
      }),
      width: 200,
      CellComponent: TableFieldCells.useSelectCellFactory(
        useStaticAttachmentTypes
      ),
    },
    {
      datakey: 'file.name',
      name: intl.formatMessage({
        id: 'ES__ATTACHMENTS__COLUMN__FILE_NAME',
        defaultMessage: 'Soubor',
      }),
      width: 200,
      CellComponent: TableFieldCells.TextCell,
    },
  ];

  const labelColumn: TableFieldColumn<DocumentAttachment> = {
    datakey: 'label',
    name: intl.formatMessage({
      id: 'ES__ATTACHMENTS__COLUMN__LABEL',
      defaultMessage: 'Označení',
    }),
    width: 100,
    CellComponent: TableFieldCells.TextCell,
  };

  const downloadColumn: TableFieldColumn<DocumentAttachment> = {
    datakey: 'id',
    name: '',
    width: 140,
    CellComponent: function Cell({ rowValue }) {
      const { getFileUrl } = useContext(FilesContext);

      return (
        <>
          {rowValue?.file !== undefined && (
            <Tooltip
              placement="top"
              type="HOVER"
              title={intl.formatMessage({
                id: 'ES__ATTACHMENTS__DOWNLOAD',
                defaultMessage: 'Stáhnout',
              })}
            >
              <span style={{ display: 'inline-flex' }}>
                <Button
                  download={true}
                  size="small"
                  classes={{ root: classes.downloadButton }}
                  variant="text"
                  href={getFileUrl(rowValue.file.id)}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <GetAppIcon />
                </Button>
              </span>
            </Tooltip>
          )}
        </>
      );
    },
  };

  return useMemo(
    () =>
      agenda === Agenda.COURT
        ? [downloadColumn, labelColumn, ...commonColumns]
        : [downloadColumn, ...commonColumns],
    [intl]
  );
}
